import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  BookOpen,
  Brain,
  GraduationCap,
  CheckCircle,
  Clock,
  Target,
  Users,
  Award,
  ArrowRight,
  Stethoscope,
  Menu,
  X,
  Shield,
  UserCog,
  User
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_academia-sabeneval/artifacts/ai0i1vi4_2F29CCC3-E812-45AF-8948-674464C4314C.PNG";

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    { icon: Brain, title: "Pensamiento Matemático", description: "Ejercicios y problemas diseñados específicamente para el EXANI II" },
    { icon: BookOpen, title: "Comprensión Lectora", description: "Textos y análisis para desarrollar habilidades de lectura crítica" },
    { icon: Target, title: "Redacción Indirecta", description: "Gramática, ortografía y corrección de textos" },
    { icon: Clock, title: "Simuladores Completos", description: "Practica con exámenes que simulan las condiciones reales" }
  ];

  const benefits = [
    "Contenido actualizado según el CENEVAL",
    "9 módulos de estudio completos",
    "3 simuladores tipo EXANI II",
    "Estrategias probadas para el examen",
    "Enfocado en el área de la salud",
    "Acceso ilimitado al material"
  ];

  const accessTypes = [
    { icon: Shield, title: "Administrador", description: "Gestión completa del sistema", path: "/admin", color: "purple" },
    { icon: UserCog, title: "Profesor", description: "Subir contenido y gestionar material", path: "/profesor", color: "blue" },
    { icon: User, title: "Alumno", description: "Acceder al curso y simuladores", path: "/mi-curso", color: "teal" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <a href="/" className="flex items-center gap-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="Sabeneval" className="h-10 md:h-12 w-auto" />
              <span className="font-heading font-bold text-xl md:text-2xl text-purple-700">SABENEVAL</span>
            </a>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#diagnostico" className="text-slate-600 hover:text-purple-600 transition-colors font-medium">Diagnóstico</a>
              <a href="#curso" className="text-slate-600 hover:text-purple-600 transition-colors font-medium">Curso</a>
              <a href="#acceso" className="text-slate-600 hover:text-purple-600 transition-colors font-medium">Acceso</a>
              <Button onClick={() => navigate("/examen-diagnostico")} className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-6" data-testid="nav-start-exam-btn">
                Iniciar Diagnóstico
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-100">
              <div className="flex flex-col gap-4">
                <a href="#diagnostico" className="text-slate-600 hover:text-purple-600 py-2">Diagnóstico</a>
                <a href="#curso" className="text-slate-600 hover:text-purple-600 py-2">Curso</a>
                <a href="#acceso" className="text-slate-600 hover:text-purple-600 py-2">Acceso</a>
                <Button onClick={() => navigate("/examen-diagnostico")} className="bg-purple-600 hover:bg-purple-700 text-white rounded-full w-full" data-testid="mobile-start-exam-btn">
                  Iniciar Diagnóstico
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 hero-gradient">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium">
                <Stethoscope className="w-4 h-4" />
                Área de la Salud
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Prepárate para el <span className="text-purple-600">EXANI II</span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-lg">
                La plataforma más completa para tu ingreso a carreras del área de la salud. Conoce tu nivel actual y mejora tu puntaje.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button onClick={() => navigate("/examen-diagnostico")} className="bg-purple-600 hover:bg-purple-700 text-white rounded-full h-12 px-8 text-base font-medium transition-all active:scale-95" data-testid="hero-start-exam-btn">
                  Iniciar Examen Diagnóstico
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button onClick={() => navigate("/curso")} variant="outline" className="rounded-full h-12 px-8 text-base font-medium border-slate-300 hover:bg-slate-50" data-testid="hero-view-course-btn">
                  Ver Curso Completo
                </Button>
              </div>

              <div className="flex items-center gap-6 pt-4 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  <span>+500 estudiantes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-teal-500" />
                  <span>Alta tasa de éxito</span>
                </div>
              </div>
            </div>

            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-purple-900/10">
                <img src="https://images.unsplash.com/photo-1766297248122-5957c51b1f7c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Estudiantes de medicina" className="w-full h-auto object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/30 to-transparent"></div>
              </div>
              
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-slate-100 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Diagnóstico Gratuito</p>
                    <p className="text-xs text-slate-500">Conoce tu nivel ahora</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Access Section - NEW */}
      <section id="acceso" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">Acceso a la Plataforma</h2>
            <p className="text-lg text-slate-600">Selecciona tu tipo de acceso para ingresar al sistema</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {accessTypes.map((access, index) => (
              <Card key={index} className={`border-2 hover:border-${access.color}-300 hover:shadow-xl transition-all cursor-pointer group`} onClick={() => navigate(access.path)} data-testid={`access-${access.title.toLowerCase()}-btn`}>
                <CardContent className="p-8 text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                    access.color === "purple" ? "bg-purple-100 group-hover:bg-purple-600" :
                    access.color === "blue" ? "bg-blue-100 group-hover:bg-blue-600" :
                    "bg-teal-100 group-hover:bg-teal-600"
                  } transition-colors`}>
                    <access.icon className={`w-8 h-8 ${
                      access.color === "purple" ? "text-purple-600 group-hover:text-white" :
                      access.color === "blue" ? "text-blue-600 group-hover:text-white" :
                      "text-teal-600 group-hover:text-white"
                    } transition-colors`} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">{access.title}</h3>
                  <p className="text-slate-500 text-sm">{access.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Diagnostic Section */}
      <section id="diagnostico" className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">Examen Diagnóstico EXANI II</h2>
            <p className="text-lg text-slate-600">Evalúa tu nivel actual antes de comenzar tu preparación.</p>
          </div>

          <Card className="max-w-2xl mx-auto border-purple-100 shadow-lg" data-testid="diagnostic-card">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Brain className="w-10 h-10 text-purple-600" />
              </div>
              
              <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-4">¿Estás listo para conocer tu nivel?</h3>
              <p className="text-slate-600 mb-6">El examen diagnóstico evalúa tres áreas principales: Pensamiento Matemático, Comprensión Lectora y Redacción Indirecta.</p>

              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-purple-600">30</p>
                  <p className="text-sm text-slate-500">Preguntas</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-purple-600">45</p>
                  <p className="text-sm text-slate-500">Minutos</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-purple-600">3</p>
                  <p className="text-sm text-slate-500">Áreas</p>
                </div>
              </div>

              <Button onClick={() => navigate("/examen-diagnostico")} className="bg-purple-600 hover:bg-purple-700 text-white rounded-full h-12 px-10 text-base font-medium" data-testid="diagnostic-start-btn">
                Iniciar Examen Diagnóstico
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">Contenido del Programa</h2>
            <p className="text-lg text-slate-600">Nuestro curso cubre todas las áreas evaluadas en el EXANI II</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-slate-100 hover:border-purple-100 hover:shadow-lg transition-all duration-300 group" data-testid={`feature-card-${index}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-600 transition-colors">
                    <feature.icon className="w-6 h-6 text-purple-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Course Section */}
      <section id="curso" className="py-16 md:py-24 bg-gradient-to-br from-purple-600 to-purple-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="inline-block bg-white/20 text-white px-4 py-1 rounded-full text-sm font-medium mb-6">Programa Completo</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Curso Completo de Preparación EXANI II</h2>
              <p className="text-purple-100 text-lg mb-6">Programa integral diseñado para estudiantes que desean ingresar a carreras del área de la salud.</p>

              <div className="mb-2">
                <span className="inline-block bg-yellow-400 text-yellow-900 text-sm font-bold px-3 py-1 rounded-full">PRECIO ESPECIAL DE LANZAMIENTO</span>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-bold">$2,599</span>
                <span className="text-purple-200 text-xl">MXN</span>
              </div>
              <p className="text-purple-200 text-sm mb-8">Cupo limitado para los primeros estudiantes.</p>

              <div className="space-y-3 mb-8">
                {benefits.slice(0, 4).map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0" />
                    <span className="text-purple-100">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={() => navigate("/inscripcion")} className="bg-white text-purple-700 hover:bg-purple-50 rounded-full h-12 px-8 text-base font-medium" data-testid="course-enroll-btn">
                  Inscribirse al Curso
                </Button>
                <Button onClick={() => navigate("/curso")} variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full h-12 px-8 text-base font-medium" data-testid="course-details-btn">
                  Ver Detalles
                </Button>
              </div>
            </div>

            <div className="relative">
              <img src="https://images.unsplash.com/photo-1701576766277-c6160505581d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" alt="Estudiante preparándose" className="rounded-2xl shadow-2xl" />
              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-lg p-4 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">9 Módulos</p>
                    <p className="text-xs text-slate-500">Contenido completo</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">¿Por qué elegir Sabeneval?</h2>
            <p className="text-lg text-slate-600">Nuestra metodología está diseñada para maximizar tu rendimiento</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-4 p-6 rounded-xl bg-slate-50 hover:bg-purple-50 transition-colors" data-testid={`benefit-item-${index}`}>
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <p className="text-slate-700 font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">Comienza tu preparación hoy</h2>
          <p className="text-lg text-slate-600 mb-8">Da el primer paso hacia tu carrera en el área de la salud.</p>
          <Button onClick={() => navigate("/examen-diagnostico")} className="bg-purple-600 hover:bg-purple-700 text-white rounded-full h-14 px-10 text-lg font-medium" data-testid="cta-start-exam-btn">
            Iniciar Examen Diagnóstico Gratuito
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Sabeneval" className="h-12 w-auto" />
              <span className="font-heading font-bold text-2xl text-white">SABENEVAL</span>
            </div>
            <div className="flex items-center gap-6 text-purple-200 text-sm">
              <a href="mailto:sabeneval@gmail.com" className="hover:text-white transition-colors">sabeneval@gmail.com</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-purple-800 text-center text-purple-300 text-sm">
            © 2024 Sabeneval. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
